/**
 * What an engine sounds like, from what it is doing: the pure half of the
 * engine sound. The other half, `engineDsp.ts`, turns these numbers into
 * samples, the way a car's engine turns fuel into noise: a pulse of exhaust
 * at every cylinder's firing, rung through the pipe and muffled by the box.
 *
 * So the pitch is the firing rate. A four-stroke fires each cylinder once in
 * two turns of the crank, so a four-cylinder fires twice a turn: 800 rpm at
 * idle is 27 pulses a second, the low burble, and 6000 rpm is 200, the howl.
 * A single-cylinder dirt bike fires once in two turns, which is why it
 * thuds at idle and rasps at the top.
 *
 * The revs come from the gearbox the way the drive's own pull does
 * (`three/Vehicle.ts`, `pull`): the road speed over the gear's top, held up
 * off the line by the clutch slipping. A change up drops them to the next
 * gear's; the throttle as the engine has it (the turbo's lag, `spool`) is the
 * load, and the load is how hard each pulse is and how bright.
 */

export type EngineKind = 'petrol' | 'diesel' | 'electric' | 'truck' | 'bus' | 'moto' | 'escooter' | 'heli';

export interface EngineProfile {
  /** Cylinders, and strokes a cycle: a four-stroke fires each once in two turns. */
  cylinders: number;
  strokes: 2 | 4;
  /** Revs at idle and at the limiter, rpm. */
  idle: number;
  redline: number;
  /** The exhaust's first resonance, Hz: a long pipe booms low, a short one barks. */
  pipe: number;
  /** The silencer's cut-off at idle, Hz, opened up by load and revs. */
  muffle: number;
  /** A diesel's combustion knock, 0..1: the clatter at every firing. */
  clatter: number;
  /** The turbo's whistle, 0..1. */
  turbo: number;
  /** How hard the exhaust note is driven into distortion, 0..1: a bike's rasp. */
  rasp: number;
  /** Chance a firing on the overrun pops in the pipe. */
  pops: number;
  /** An electric motor's whine, 0..1. */
  whine: number;
  /** Uneven cylinders: how much each pulse differs from the last, 0..1. */
  rough: number;
  /** Overall level, so a lorry is louder than a hatchback. */
  level: number;
}

export const ENGINES: Record<EngineKind, EngineProfile> = {
  petrol: { cylinders: 4, strokes: 4, idle: 800, redline: 6500, pipe: 110, muffle: 520, clatter: 0.05, turbo: 0, rasp: 0.15, pops: 0.01, whine: 0, rough: 0.12, level: 0.55 },
  diesel: { cylinders: 4, strokes: 4, idle: 760, redline: 4600, pipe: 95, muffle: 460, clatter: 0.55, turbo: 0.35, rasp: 0.1, pops: 0, whine: 0, rough: 0.18, level: 0.6 },
  electric: { cylinders: 0, strokes: 4, idle: 0, redline: 12000, pipe: 0, muffle: 0, clatter: 0, turbo: 0, rasp: 0, pops: 0, whine: 1, rough: 0, level: 0.4 },
  truck: { cylinders: 6, strokes: 4, idle: 600, redline: 2200, pipe: 62, muffle: 330, clatter: 0.45, turbo: 0.8, rasp: 0.2, pops: 0, whine: 0, rough: 0.15, level: 0.9 },
  bus: { cylinders: 6, strokes: 4, idle: 650, redline: 2400, pipe: 70, muffle: 300, clatter: 0.4, turbo: 0.6, rasp: 0.12, pops: 0, whine: 0, rough: 0.14, level: 0.8 },
  moto: { cylinders: 1, strokes: 4, idle: 1700, redline: 11500, pipe: 175, muffle: 900, clatter: 0.1, turbo: 0, rasp: 0.6, pops: 0.12, whine: 0, rough: 0.3, level: 0.7 },
  // An e-scooter's hub motor: a thin whine that climbs with the wheel, and
  // little else — no exhaust, hardly any tyre roar, no warning hum.
  escooter: { cylinders: 0, strokes: 4, idle: 0, redline: 1100, pipe: 0, muffle: 0, clatter: 0, turbo: 0, rasp: 0, pops: 0, whine: 1.8, rough: 0, level: 0.45 },
  // A light turbine helicopter: the turbine screams at a steady speed, the
  // rotor's blades go by at a steady rate and thump harder under load.
  heli: { cylinders: 0, strokes: 4, idle: 0, redline: 0, pipe: 0, muffle: 1400, clatter: 0, turbo: 0, rasp: 0.2, pops: 0, whine: 0, rough: 0.05, level: 0.9 },
};

/** The rotor's blade passes a second at full speed: four blades at ~330 rpm. */
export const BLADE_PASS = 22;

/** An e-scooter's 10-inch hub motor: its turns a minute per m/s of road speed, and its magnet pole pairs. */
export const SCOOTER_RPM_PER_MS = 75;
export const SCOOTER_POLE_PAIRS = 15;

/** Each gear's top speed, m/s, for the dirt bike: its physics has no gearbox, its sound does. */
export const MOTO_GEARS = [9, 15, 21, 28, 35, 44] as const;

export interface EngineInput {
  /** The gear, 1-based, and each gear's top speed, m/s. An electric car's are ignored. */
  gear: number;
  tops: readonly number[];
  /** Along the heading, m/s; negative backing up. */
  speed: number;
  /** The pedal, -1..1 (the collective, for the helicopter). */
  throttle: number;
  /** The throttle as the engine has it, after the turbo's lag, 0..1. */
  spool: number;
  /** Seconds left of a gear change. */
  shifting: number;
  /** Running at all: false with the key out, or the tank dry. */
  running: boolean;
}

export interface EngineNote {
  /** Crankshaft speed, rpm (the rotor's, for the helicopter). */
  rpm: number;
  /** 0 at idle .. 1 at the limiter. */
  revs: number;
  /** Exhaust pulses a second: the note's fundamental. Blade passes for the helicopter; motor turns for an electric car. */
  firing: number;
  /** How hard it works, 0 on the overrun .. 1 flat out. */
  load: number;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Firing pulses a second at `rpm`. */
export function firingRate(profile: EngineProfile, rpm: number): number {
  return (rpm / 60) * profile.cylinders * (2 / profile.strokes);
}

/**
 * Where in its revs a geared engine is: the road speed over the gear's top,
 * held up off the line by the clutch, free-revving in neutral when stopped
 * with the throttle down, idling stopped without.
 */
export function revsOf(input: EngineInput): number {
  const { gear, tops, speed, throttle } = input;
  const v = Math.abs(speed);
  if (tops.length === 0) return 0;
  if (speed < -0.2) return clamp01(v / tops[0]) * 0.8;
  const top = tops[Math.max(0, Math.min(tops.length - 1, gear - 1))];
  const turning = v / top;
  // Pulling away: the clutch slips, the revs held up by the throttle.
  const slip = gear === 1 && throttle > 0 ? 0.22 + 0.3 * throttle : 0;
  return clamp01(Math.max(turning, slip));
}

/** What `kind` is doing in `input`: its revs, firing rate and load. */
export function engineNote(kind: EngineKind, input: EngineInput): EngineNote {
  const p = ENGINES[kind];
  if (!input.running) return { rpm: 0, revs: 0, firing: 0, load: 0 };
  if (kind === 'heli') {
    // The rotor holds its speed; the collective changes the blades' bite.
    const load = clamp01(0.45 + 0.55 * input.throttle);
    return { rpm: (BLADE_PASS / 4) * 60, revs: 1, firing: BLADE_PASS, load };
  }
  if (kind === 'electric') {
    // A single fixed gear: the motor turns with the wheels, about 10 000 rpm at 130 km/h.
    const rpm = Math.abs(input.speed) * 280;
    const load = input.throttle > 0 ? clamp01(input.throttle) : input.throttle < 0 ? 0.3 * -input.throttle : 0;
    return { rpm, revs: clamp01(rpm / p.redline), firing: rpm / 60, load };
  }
  if (kind === 'escooter') {
    // A hub motor is the wheel: a 10-inch one turns 75 rpm per m/s, and its
    // fifteen pole pairs give the whine's note — about 250 Hz flat out.
    const rpm = Math.abs(input.speed) * SCOOTER_RPM_PER_MS;
    const load = input.throttle > 0 ? clamp01(input.throttle) : input.throttle < 0 ? 0.2 * -input.throttle : 0;
    return { rpm, revs: clamp01(rpm / p.redline), firing: (rpm / 60) * SCOOTER_POLE_PAIRS, load };
  }
  const revs = revsOf(input);
  const rpm = p.idle + revs * (p.redline - p.idle);
  // The overrun: throttle shut or braking, the engine driven by the wheels.
  // Mid-change the clutch is out and the pull is cut.
  const load = input.shifting > 0 || input.throttle <= 0 ? 0 : clamp01(Math.max(input.spool, 0.15) * input.throttle);
  return { rpm, revs, firing: firingRate(p, rpm), load };
}

/**
 * The dirt bike's gear for `speed`, from the one it was in: up near the top
 * of a gear, down well below the one before, so it does not hunt between two.
 */
export function motoGear(speed: number, gear: number): number {
  const v = Math.abs(speed);
  let g = Math.max(1, Math.min(MOTO_GEARS.length, gear));
  while (g < MOTO_GEARS.length && v > MOTO_GEARS[g - 1] * 0.92) g++;
  while (g > 1 && v < MOTO_GEARS[g - 2] * 0.62) g--;
  return g;
}

/**
 * The Doppler factor for a source closing at `closing` m/s (negative going
 * away): a car at 90 km/h drops about 15% in pitch as it passes.
 */
export function dopplerFactor(closing: number): number {
  const c = 343;
  return c / Math.max(c * 0.5, c - Math.max(-c * 0.5, Math.min(c * 0.5, closing)));
}

/**
 * How air dulls a sound with distance, as a low-pass cut-off, Hz: a
 * hatchback at 10 m is bright, at 200 m a muffled rumble.
 */
export function airCutoff(distance: number): number {
  return Math.max(500, Math.min(18000, 18000 / (1 + distance / 35)));
}
